import { useRef, useEffect } from 'react';
import { safeWindow } from '../window.ts';

// From https://usehooks.com/useEventListener/

export const useEventListener = (
  eventName: string,
  handler: (e: Event) => void,
  element: HTMLElement | Window | Document | undefined = safeWindow(),
  options: {
    /**
     * Whether the event listener should be attached or not.
     * Defaults to true.
     */
    enabled?: boolean;
    passive?: boolean;
  } = {}
) => {
  const { enabled = true, passive } = options;

  // Create a ref that stores handler
  const savedHandler = useRef(handler);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      if (element) {
        // Create event listener that calls handler function stored in ref
        const eventListener = (event: Event) => savedHandler.current(event);

        // Add event listener
        if (enabled) {
          element.addEventListener(eventName, eventListener, { passive });
        }

        // Remove event listener on cleanup
        return () => {
          if (enabled) {
            element.removeEventListener(eventName, eventListener);
          }
        };
      }
    },
    [eventName, element, enabled, passive] // Re-run if eventName or element changes
  );
};
